import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Users, Building2, Inbox, CheckCircle2, XCircle, Loader2, ExternalLink, MessageSquare, Search, Trash2, Star, FileText, Plus, Pencil, Eye, Calendar, Clock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import PhotoUploader from "@/components/PhotoUploader";

export default function AdminPanel() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#064E3B] flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981]">Admin Panel</div>
          <h1 className="font-display text-2xl font-semibold">IV Therapy Finder — Admin</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <StatTile label="Active providers" value={stats.active_providers} />
          <StatTile label="Pending review" value={stats.pending_providers} accent={stats.pending_providers > 0 ? "amber" : "default"} />
          <StatTile label="Pending claims" value={stats.pending_claims} accent={stats.pending_claims > 0 ? "amber" : "default"} />
          <StatTile label="Users" value={stats.total_users} />
          <StatTile label="Total reviews" value={stats.total_reviews} />
          <StatTile label="Total leads" value={stats.total_leads} />
        </div>
      )}

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="admin-tab-pending"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Pending Listings</TabsTrigger>
          <TabsTrigger value="claims" data-testid="admin-tab-claims"><Shield className="w-3.5 h-3.5 mr-1.5" /> Claim Requests</TabsTrigger>
          <TabsTrigger value="all-providers" data-testid="admin-tab-providers"><Building2 className="w-3.5 h-3.5 mr-1.5" /> All Providers</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews"><MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Reviews</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog"><FileText className="w-3.5 h-3.5 mr-1.5" /> Blog</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="w-3.5 h-3.5 mr-1.5" /> Users</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-6"><PendingProviders /></TabsContent>
        <TabsContent value="claims" className="mt-6"><Claims /></TabsContent>
        <TabsContent value="all-providers" className="mt-6"><AllProviders /></TabsContent>
        <TabsContent value="reviews" className="mt-6"><ReviewsModeration /></TabsContent>
        <TabsContent value="blog" className="mt-6"><BlogManager /></TabsContent>
        <TabsContent value="users" className="mt-6"><UserList /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  const colors = { amber: "text-amber-600", default: "text-stone-900" };
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4">
      <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-1">{label}</div>
      <div className={`font-display text-2xl font-semibold ${colors[accent] || colors.default}`}>{value}</div>
    </div>
  );
}

function PendingProviders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { load(); }, []);
  const load = () => {
    setLoading(true);
    api.get("/admin/providers?status_filter=pending").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  const approve = async (id) => {
    await api.post(`/admin/providers/${id}/approve`);
    toast.success("Provider approved & published");
    load();
  };
  const reject = async (id) => {
    await api.post(`/admin/providers/${id}/reject`);
    toast.success("Provider rejected");
    load();
  };
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  if (items.length === 0) return <div className="text-center py-16 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">No pending listings — all caught up.</div>;
  return (
    <div className="space-y-3">
      {items.map((p) => (
        <div key={p.id} className="bg-white rounded-xl border border-stone-200 p-5 flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1">
            <div className="font-display text-lg font-semibold">{p.name}</div>
            <div className="text-sm text-stone-500">{p.city}, {p.state} · {p.type} · {p.phone}</div>
            <p className="text-sm text-stone-600 mt-2 line-clamp-2">{p.description || <em className="text-stone-400">No description provided.</em>}</p>
            <div className="text-xs text-stone-400 mt-2">Submitted {new Date(p.created_at).toLocaleString()}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-[#10B981] hover:bg-[#0c9970]" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}>
              <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}>
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Claims() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { load(); }, []);
  const load = () => {
    setLoading(true);
    api.get("/admin/claims").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  const approve = async (id) => { await api.post(`/admin/claims/${id}/approve`); toast.success("Claim approved"); load(); };
  const reject = async (id) => { await api.post(`/admin/claims/${id}/reject`); toast.success("Claim rejected"); load(); };
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  if (items.length === 0) return <div className="text-center py-16 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">No claim requests.</div>;
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <div key={c.id} className="bg-white rounded-xl border border-stone-200 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-display text-lg font-semibold">{c.provider_name}</div>
                <Badge variant="outline" className={
                  c.status === "approved" ? "border-[#10B981] text-[#10B981]" :
                  c.status === "rejected" ? "border-stone-400 text-stone-400" :
                  "border-amber-500 text-amber-600"
                }>{c.status}</Badge>
              </div>
              <div className="text-sm text-stone-500 mb-2">{c.provider_city}</div>
              <div className="text-sm text-stone-700 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                <div><strong>Claimant:</strong> {c.full_name} ({c.user_email})</div>
                <div><strong>Role:</strong> {c.role_in_business}</div>
                <div><strong>Verify via:</strong> {c.verification_method}</div>
                {c.business_phone && <div><strong>Phone:</strong> {c.business_phone}</div>}
              </div>
              {c.notes && <p className="text-sm text-stone-600 italic mt-2">"{c.notes}"</p>}
              <div className="text-xs text-stone-400 mt-2">Submitted {new Date(c.created_at).toLocaleString()}</div>
            </div>
            {c.status === "pending" && (
              <div className="flex gap-2 shrink-0">
                <Button size="sm" className="bg-[#10B981] hover:bg-[#0c9970]" onClick={() => approve(c.id)} data-testid={`approve-claim-${c.id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => reject(c.id)}>Reject</Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function AllProviders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/providers").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50">
          <tr><th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600">Business</th><th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600">Location</th><th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600">Status</th><th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600">Reviews</th><th></th></tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-t border-stone-100">
              <td className="p-3"><div className="font-medium">{p.name}</div><div className="text-xs text-stone-500">{p.type}</div></td>
              <td className="p-3 text-stone-600">{p.city}, {p.state}</td>
              <td className="p-3"><Badge variant="outline" className="capitalize">{p.status}</Badge></td>
              <td className="p-3">{p.review_count} · ★{p.avg_rating?.toFixed(1) || "—"}</td>
              <td className="p-3"><Link to={`/listing/${p.slug}`} target="_blank" className="text-[#064E3B] hover:underline inline-flex items-center gap-1 text-xs"><ExternalLink className="w-3 h-3" />View</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" />;
  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50"><tr><th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Name</th><th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Email</th><th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Role</th><th className="text-left p-3 text-xs uppercase tracking-wider text-stone-600">Joined</th></tr></thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} className="border-t border-stone-100">
              <td className="p-3 font-medium">{u.name}</td>
              <td className="p-3 text-stone-600">{u.email}</td>
              <td className="p-3"><Badge variant="outline" className="capitalize">{u.role}</Badge></td>
              <td className="p-3 text-stone-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ReviewsModeration() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [rating, setRating] = useState("all");
  const [sort, setSort] = useState("newest");
  const [expandedId, setExpandedId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (providerFilter.trim()) params.set("provider", providerFilter.trim());
    if (rating && rating !== "all") params.set("rating", rating);
    if (sort) params.set("sort", sort);
    params.set("limit", "200");
    try {
      const { data } = await api.get(`/admin/reviews?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sort, rating]);

  const onSearchSubmit = (e) => { e.preventDefault(); load(); };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/reviews/${toDelete.id}`, { data: { reason } });
      toast.success("Review deleted");
      setItems((cur) => cur.filter((r) => r.id !== toDelete.id));
      setTotal((t) => Math.max(0, t - 1));
      setToDelete(null);
      setReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete review");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <form onSubmit={onSearchSubmit} className="bg-white rounded-2xl border border-stone-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-4">
          <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Search review text</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='e.g. "rude", "overcharged"'
              className="pl-9"
              data-testid="reviews-search-q"
            />
          </div>
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Provider name</Label>
          <Input
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            placeholder="e.g. Acme IV Clinic"
            data-testid="reviews-search-provider"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Rating</Label>
          <Select value={rating} onValueChange={setRating}>
            <SelectTrigger data-testid="reviews-filter-rating"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              <SelectItem value="1">1 star</SelectItem>
              <SelectItem value="2">2 stars</SelectItem>
              <SelectItem value="3">3 stars</SelectItem>
              <SelectItem value="4">4 stars</SelectItem>
              <SelectItem value="5">5 stars</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Sort</Label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="reviews-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="rating_low">Rating low → high</SelectItem>
              <SelectItem value="rating_high">Rating high → low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1">
          <Button type="submit" className="w-full bg-[#064E3B] hover:bg-[#053d2f]" data-testid="reviews-search-submit">
            Search
          </Button>
        </div>
      </form>

      <div className="text-xs text-stone-500 mb-3" data-testid="reviews-count">
        {loading ? "Loading…" : `${total} ${total === 1 ? "review" : "reviews"} match`}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">
          No reviews match these filters.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[20%]">Provider</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600">Review</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[80px]">Rating</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[18%]">Submitted by</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[120px]">Date</th>
                <th className="text-right p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[120px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const expanded = expandedId === r.id;
                const excerpt = r.body && r.body.length > 140 ? r.body.slice(0, 140) + "…" : r.body;
                return (
                  <tr key={r.id} className="border-t border-stone-100 align-top" data-testid={`review-row-${r.id}`}>
                    <td className="p-3">
                      <Link to={`/listing/${r.provider_slug}`} target="_blank" className="font-medium text-stone-900 hover:text-[#064E3B] hover:underline inline-flex items-center gap-1">
                        {r.provider_name}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                      <div className="text-xs text-stone-500 mt-0.5">{r.provider_city}, {r.provider_state}</div>
                    </td>
                    <td className="p-3">
                      {r.title && <div className="font-medium text-stone-900 mb-1">{r.title}</div>}
                      <div className="text-stone-700 leading-relaxed">
                        {expanded ? r.body : excerpt}
                      </div>
                      {r.body && r.body.length > 140 && (
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : r.id)}
                          className="mt-1 text-xs font-semibold text-[#064E3B] hover:underline"
                          data-testid={`review-toggle-${r.id}`}
                        >
                          {expanded ? "Collapse" : "Read full review"}
                        </button>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="inline-flex items-center gap-1">
                        <Star className={`w-3.5 h-3.5 ${r.rating <= 2 ? "fill-rose-500 text-rose-500" : "fill-[#D4A373] text-[#D4A373]"}`} />
                        <span className="font-semibold">{r.rating}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-stone-900">{r.user_name || "Anonymous"}</div>
                      {r.user_email && <div className="text-xs text-stone-500">{r.user_email}</div>}
                    </td>
                    <td className="p-3 text-xs text-stone-500">
                      {new Date(r.created_at).toLocaleDateString()}
                      <div className="text-[10px] text-stone-400">{new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                        onClick={() => { setToDelete(r); setReason(""); }}
                        data-testid={`review-delete-${r.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) { setToDelete(null); setReason(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-600">Delete this review?</DialogTitle>
            <DialogDescription>
              This will permanently remove the review and recalculate the provider's average rating. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {toDelete && (
            <div className="bg-stone-50 rounded-lg border border-stone-200 p-3 my-2 text-sm">
              <div className="text-xs text-stone-500 mb-1">{toDelete.provider_name} · {toDelete.rating}★ · {toDelete.user_name}</div>
              {toDelete.title && <div className="font-medium text-stone-900 mb-1">{toDelete.title}</div>}
              <div className="text-stone-700 line-clamp-3">{toDelete.body}</div>
            </div>
          )}
          <div>
            <Label htmlFor="delete-reason" className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">
              Reason (optional, kept in audit log)
            </Label>
            <Textarea
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Inappropriate language, Spam, Competitor review"
              rows={2}
              data-testid="review-delete-reason"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setToDelete(null); setReason(""); }} disabled={deleting}>
              Cancel
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="review-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Trash2 className="w-4 h-4 mr-1.5" /> Delete review</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY_POST = {
  title: "",
  excerpt: "",
  body: "",
  category: "IV Therapy",
  hero_image: "",
  author: "IVTF Editorial",
  tags: [],
  status: "draft",
};

const BLOG_CATEGORIES = ["IV Therapy", "Wellness", "Recovery", "Hydration", "Athletic Performance", "Beauty & Skin", "Mental Health", "Industry News"];

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, otherwise full post
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch (err) {
      toast.error("Could not load blog posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (postSummary) => {
    try {
      const { data } = await api.get(`/admin/blog/${postSummary.id}`);
      setEditing(data);
      setEditorOpen(true);
    } catch {
      toast.error("Could not load post");
    }
  };

  const handleSaved = (saved, wasNew) => {
    if (wasNew) {
      setPosts((p) => [{ ...saved, body: undefined }, ...p]);
    } else {
      setPosts((p) => p.map((it) => (it.id === saved.id ? { ...it, ...saved, body: undefined } : it)));
    }
    setEditorOpen(false);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/blog/${toDelete.id}`);
      setPosts((p) => p.filter((it) => it.id !== toDelete.id));
      toast.success("Post deleted");
      setToDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete post");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-stone-900">Blog Posts</h2>
          <p className="text-xs text-stone-500 mt-0.5">{loading ? "Loading…" : `${posts.length} total`}</p>
        </div>
        <Button className="bg-[#064E3B] hover:bg-[#053d2f]" onClick={openNew} data-testid="new-post-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New post
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#064E3B]" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200 text-stone-500 text-sm">
          No blog posts yet. Click "New post" to write your first article.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600">Title</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[120px]">Status</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[140px]">Category</th>
                <th className="text-left p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[130px]">Date</th>
                <th className="text-right p-3 font-semibold text-xs uppercase tracking-wider text-stone-600 w-[180px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.id} className="border-t border-stone-100 align-top" data-testid={`blog-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-medium text-stone-900">{p.title}</div>
                    <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">{p.excerpt}</div>
                    <div className="text-[10px] text-stone-400 mt-1">/{p.slug}</div>
                  </td>
                  <td className="p-3">
                    <Badge className={p.status === "draft" ? "bg-stone-100 text-stone-600 hover:bg-stone-100 border-0" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0"}>
                      {p.status === "draft" ? "Draft" : "Published"}
                    </Badge>
                  </td>
                  <td className="p-3 text-stone-700">{p.category}</td>
                  <td className="p-3 text-xs text-stone-500">{new Date(p.published_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right whitespace-nowrap space-x-1">
                    {p.status === "published" && (
                      <Button size="sm" variant="outline" className="h-8" asChild data-testid={`blog-view-${p.id}`}>
                        <Link to={`/blog/${p.slug}`} target="_blank"><Eye className="w-3.5 h-3.5 mr-1" /> View</Link>
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                      onClick={() => setToDelete(p)}
                      data-testid={`blog-delete-${p.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BlogEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        editing={editing}
        onSaved={handleSaved}
      />

      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-rose-600">Delete this post?</DialogTitle>
            <DialogDescription>
              "<strong>{toDelete?.title}</strong>" will be permanently removed. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={confirmDelete} disabled={deleting} data-testid="blog-delete-confirm">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Trash2 className="w-4 h-4 mr-1.5" /> Delete</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BlogEditor({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("edit"); // edit | preview

  useEffect(() => {
    if (!open) return;
    setView("edit");
    if (editing) {
      setForm({
        title: editing.title || "",
        excerpt: editing.excerpt || "",
        body: editing.body || "",
        category: editing.category || "IV Therapy",
        hero_image: editing.hero_image || "",
        author: editing.author || "IVTF Editorial",
        tags: editing.tags || [],
        status: editing.status || "draft",
      });
    } else {
      setForm(EMPTY_POST);
    }
  }, [open, editing]);

  const save = async (statusOverride) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!form.excerpt.trim()) {
      toast.error("Excerpt is required (1–2 lines used in listings & SEO)");
      return;
    }
    const cleanBody = (form.body || "").replace(/^<p>(\s|<br\s*\/?>)*<\/p>$/i, "").trim();
    if (!cleanBody) {
      toast.error("Body cannot be empty");
      return;
    }
    setSaving(true);
    const payload = { ...form, body: cleanBody };
    if (statusOverride) payload.status = statusOverride;
    try {
      let saved;
      let wasNew = false;
      if (editing?.id) {
        const { data } = await api.put(`/blog/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/blog", payload);
        saved = data;
        wasNew = true;
      }
      toast.success(saved.status === "published" ? "Post published" : "Draft saved");
      onSaved(saved, wasNew);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save post");
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (urls) => setForm((f) => ({ ...f, hero_image: urls[0] || "" }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="blog-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Edit post" : "New post"}</DialogTitle>
          <DialogDescription className="sr-only">Compose or update a blog post</DialogDescription>
        </DialogHeader>

        {/* Edit/Preview toggle */}
        <div className="flex items-center justify-between border-b border-stone-200 pb-3 mb-3">
          <div className="inline-flex bg-stone-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setView("edit")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${view === "edit" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
              data-testid="editor-view-edit"
            >
              <Pencil className="w-3.5 h-3.5 inline mr-1.5" /> Edit
            </button>
            <button
              type="button"
              onClick={() => setView("preview")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${view === "preview" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
              data-testid="editor-view-preview"
            >
              <Eye className="w-3.5 h-3.5 inline mr-1.5" /> Preview
            </button>
          </div>
          <div className="flex items-center gap-2.5 text-xs">
            <Label htmlFor="post-status" className={`uppercase tracking-wider font-semibold ${form.status === "published" ? "text-emerald-700" : "text-stone-500"}`}>
              {form.status === "published" ? "Published" : "Draft"}
            </Label>
            <Switch
              id="post-status"
              checked={form.status === "published"}
              onCheckedChange={(c) => setForm((f) => ({ ...f, status: c ? "published" : "draft" }))}
              data-testid="post-status-toggle"
            />
          </div>
        </div>

        {view === "edit" ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="A great headline that sells the article"
                className="text-base"
                data-testid="post-title"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Excerpt (used in listings & SEO)</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="One or two lines describing the article — appears in cards and meta description."
                rows={2}
                data-testid="post-excerpt"
              />
              <div className="text-[11px] text-stone-400 mt-1">{form.excerpt.length}/180</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BLOG_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Author</Label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  placeholder="IVTF Editorial"
                  data-testid="post-author"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Tags (comma-separated)</Label>
              <Input
                value={(form.tags || []).join(", ")}
                onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                placeholder="NAD+, energy, anti-aging"
                data-testid="post-tags"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Hero image</Label>
              <PhotoUploader
                photos={form.hero_image ? [form.hero_image] : []}
                onChange={updateHero}
                max={1}
                label="Upload hero"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600 font-semibold mb-1.5 block">Body</Label>
              <RichTextEditor
                value={form.body}
                onChange={(html) => setForm((f) => ({ ...f, body: html }))}
              />
            </div>
          </div>
        ) : (
          <BlogPreview post={form} />
        )}

        <DialogFooter className="border-t border-stone-200 pt-4 mt-4 flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} className="sm:mr-auto">Cancel</Button>
          <Button
            variant="outline"
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="save-draft-btn"
          >
            {saving && form.status !== "published" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save as draft"}
          </Button>
          <Button
            className="bg-[#064E3B] hover:bg-[#053d2f]"
            onClick={() => save("published")}
            disabled={saving}
            data-testid="publish-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing?.status === "published" ? "Update" : "Publish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BlogPreview({ post }) {
  return (
    <article className="bg-stone-50 rounded-xl p-6 max-h-[60vh] overflow-y-auto" data-testid="blog-preview">
      <div className="text-xs uppercase tracking-wider font-semibold text-[#064E3B] mb-2">{post.category || "IV Therapy"}</div>
      <h1 className="font-display text-3xl font-semibold text-stone-900 leading-tight mb-3">
        {post.title || <span className="text-stone-400 italic">Untitled</span>}
      </h1>
      {post.excerpt && <p className="text-lg text-stone-600 mb-4">{post.excerpt}</p>}
      <div className="flex flex-wrap items-center gap-3 text-xs text-stone-500 mb-5">
        <span>By <strong className="text-stone-700">{post.author || "IVTF Editorial"}</strong></span>
        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date().toLocaleDateString()}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {Math.max(2, ((post.body || "").replace(/<[^>]+>/g, "").split(/\s+/).length / 220) | 0)} min read</span>
      </div>
      {post.hero_image && (
        <img src={post.hero_image} alt="" className="w-full rounded-xl aspect-[21/9] object-cover mb-5" />
      )}
      {post.body ? (
        <div className="tiptap-body text-stone-800" dangerouslySetInnerHTML={{ __html: post.body }} />
      ) : (
        <p className="text-stone-400 italic">No content yet. Switch to the Edit tab to write your article.</p>
      )}
      {post.tags?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-stone-200 flex flex-wrap gap-1.5">
          {post.tags.map((t) => (
            <Badge key={t} variant="secondary" className="bg-stone-200 text-stone-700 hover:bg-stone-200 border-0 text-[11px]">#{t}</Badge>
          ))}
        </div>
      )}
    </article>
  );
}

